import React, { useState } from "react";
import { Box, Container, Typography } from "@mui/material";
import dayjs from "dayjs";
import CustomTabs from "../../common/custom-tabs";
import HeightButton from "../../common/height-button";
import typography from "../../cssUtils/typography";
import { CAMPAIGN_OVERVIEW_ENDPOINT, SINGLE_BROADCASTS_ENDPOINT } from "../../config/urls";
import { ChannelBadge, ProgressBar, StatusBadge } from "./campaignBadges";
import CampaignBroadcasts from "./CampaignBroadcasts";
import PagedListTab from "./PagedListTab";

const TABS = [
  { label: "Campaigns", value: "campaigns" },
  { label: "Single Broadcast", value: "single" },
];

const mutedText = (value) => <Typography sx={{ ...typography.bodyLarge, color: "#6B7280" }}>{value || "—"}</Typography>;

const formatDateRange = (start, end) => {
  if (!start) return "—";
  const from = dayjs(start).format("MMM D, YYYY");
  const to = dayjs(end || start).format("MMM D, YYYY");
  return from === to ? from : `${from} – ${to}`;
};

const campaignColumns = [
  {
    id: "campaign_name",
    label: "Campaign name",
    render: (row) => <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>{row.campaign_name || "—"}</Typography>,
  },
  {
    id: "channels",
    label: "Channels",
    render: (row) => (
      <Box sx={{ display: "flex", gap: 0.75 }}>
        {row.channels.map((channel) => (
          <ChannelBadge key={channel} channel={channel} />
        ))}
      </Box>
    ),
  },
  {
    id: "broadcast_count",
    label: "Broadcasts",
    render: (row) => (
      <Box>
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 700 }}>{row.broadcast_count}</Typography>
        <Typography sx={{ ...typography.bodySmall, color: "#9AA0AC" }}>{(row.targeted || 0).toLocaleString()} targeted</Typography>
      </Box>
    ),
  },
  { id: "progress", label: "Progress", render: (row) => <ProgressBar counts={row.status_counts} /> },
  { id: "created_by", label: "Created by", render: (row) => mutedText(row.created_by) },
  { id: "dates", label: "Dates", render: (row) => mutedText(formatDateRange(row.start_date, row.end_date)) },
];

const singleColumns = [
  {
    id: "name",
    label: "Broadcast",
    render: (row) => <Typography sx={{ ...typography.bodyLarge, fontWeight: 500 }}>{row.name}</Typography>,
  },
  { id: "channel", label: "Channel", render: (row) => <ChannelBadge channel={row.channel} /> },
  { id: "template_name", label: "Template", render: (row) => mutedText(row.template_name) },
  { id: "recipient", label: "Recipient", render: (row) => mutedText(row.recipient) },
  { id: "status", label: "Status", render: (row) => <StatusBadge status={row.status} /> },
  { id: "sent_at", label: "Sent at", render: (row) => mutedText(row.sent_at ? dayjs(row.sent_at).format("MMM D, YYYY, h:mm A") : null) },
  { id: "created_by", label: "Created by", render: (row) => mutedText(row.created_by) },
];

const CampaignsV2 = (props) => {
  const [activeTab, setActiveTab] = useState(props?.pageParams?.tab === "single" ? "single" : "campaigns");
  // A tab's list stays mounted (hidden) once opened, so each keeps its own search, filters and page
  const [openedTabs, setOpenedTabs] = useState({ [activeTab]: true });
  const switchTab = (value) => {
    setActiveTab(value);
    setOpenedTabs((prev) => ({ ...prev, [value]: true }));
    // The table sizes itself to the screen; let the newly shown one re-measure (a hidden table measures as 0)
    setTimeout(() => window.dispatchEvent(new Event("resize")), 0);
  };
  const canCreate = JSON.parse(localStorage.getItem("permissions") || "[]").includes("marketing_broadcast.create");
  const openSetup = (params) => props.onNavigate("campaigns_v2_setup", params);

  return (
    <Container maxWidth={false} sx={{ bgcolor: "#FAFAFA", pt: "17px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <CustomTabs value={activeTab} onChange={(event, value) => switchTab(value)} tabs={TABS} />
        {canCreate && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <HeightButton variant="outlined" title="Single/Quick broadcast" onClick={() => openSetup({ mode: "quick" })} />
            <HeightButton variant="contained" title="+ Create Campaign" onClick={() => openSetup({ mode: "create" })} />
          </Box>
        )}
      </Box>

      {openedTabs.campaigns && (
        <Box sx={{ display: activeTab === "campaigns" ? "block" : "none" }}>
        <PagedListTab
          key="campaigns"
          endpoint={CAMPAIGN_OVERVIEW_ENDPOINT}
          columns={campaignColumns}
          statusOptions={["success", "scheduled", "in_progress", "failed", "cancelled"]}
          searchPlaceholder="Search campaigns, broadcasts or templates..."
          searchTooltip="Search by campaign name, broadcast name or template name"
          emptyMessage="No campaigns found"
          isRowExpandedByDefault={(row) => row.matched_in_broadcast}
          renderExpanded={(row) => (
            <CampaignBroadcasts
              campaignId={row.id}
              onNavigate={props.onNavigate}
              onAddBroadcast={canCreate ? () => openSetup({ mode: "add", campaignId: row.id }) : null}
            />
          )}
        />
        </Box>
      )}
      {openedTabs.single && (
        <Box sx={{ display: activeTab === "single" ? "block" : "none" }}>
        <PagedListTab
          key="single"
          endpoint={SINGLE_BROADCASTS_ENDPOINT}
          columns={singleColumns}
          statusOptions={["success", "scheduled", "in_progress", "failed", "cancelled"]}
          searchPlaceholder="Search broadcasts..."
          searchTooltip="Search by name or phone number"
          emptyMessage="No single broadcasts found"
          rowKey={(row) => `${row.channel}-${row.id}`}
        />
        </Box>
      )}
    </Container>
  );
};

export default CampaignsV2;
